// src/pages/Home.js – NextScenes Home (CSS-aligned to the rest of your app)
// Build: 2026-06-07-homepage-presence-refresh-v1
// Adds: LIVE Home Spotlight (Writer / Book / Daily Words) via GET /api/home/spotlight
// Keeps: Hero + Quick Start + Four Ways + Trending + Live Feedback + Guided Tour modal
//
// Update: Live Feedback scale (B + C)
//  - C: General Feedback (no storyline required)
//  - B: Shortlists (Recent + Trending) + Search modal for All storylines
//
// Update: 2026-01-16 – Nextscenish buttons + Live Feedback scalability (Sort + Load more + Read more)
//
// Update: 2026-01-17 – Differentiate WriterStudio Reader Feedback vs Home Feedback (badges + subtle colors)
//
// Update: 2026-01-21 – Trending now comes from backend (authoritative across devices):
//   GET /api/home/trending
//
// Update: 2026-01-22 – Simplify: remove old client-side trending shaping.
//   Home consumes only /api/home/trending and stores normalized items directly.

import React, { useEffect, useMemo, useRef, useState } from "react";
import "../App.css";
import "./Home.css";

import "./LiveFeedback.css";
import {
  loadStoredStories,
  saveStoredStories,
  mergeStoredAndRemoteStories,
} from "../storyStorage";
import { api } from "../api";
import { useUser } from "../UserContext";

/* -------------------- text -------------------- */
const TEXT = {
  en: {
    heroTitle: "Next-level collaborative storytelling for readers and writers.",
    heroDesc1: "Imagination with conscience. Stories here are not written to glorify chaos. We entertain, but we also hold a mirror to truth, justice, responsibility, and the consequences of choice.",
    heroDesc2: "Start a storyline, invite scenes, and turn many imaginations into one compelling book while keeping control of your values and your vision.",
    startWriting: "Start Writing",
    browseStories: "Browse Stories",
    exploreForums: "Explore Forums",

    startHereTitle: "New to NextScenes? Begin here in 30 seconds.",
    startHereDesc: "Start here. You will understand the platform by using it, not by studying it.",
    step1: "Explore stories in Storylines",
    step2: "Open one and read what others have written",
    step3: "Continue the story or propose your own scene",
    startHereNote: "You can’t break anything. Just explore and see what unfolds.",
    startHereBtn: "Explore Stories in Storylines",

    betaHelpTitle: "Help us improve NextScenes (Beta)",
    betaHelpDesc: "What confused you? What worked well? Your voice helps shape this platform.",
    shareFeedback: "Share Feedback",

    quickStartTitle: "Quick Start",
    quickStartDesc1: "New here? Follow this path. Powerful should not feel like a maze.",
    quickStartDesc2: "You do not need to master everything. Just take one action.",
    guidedTour: "Guided Tour",
    hide: "Hide",

    qs1Title: "Create a Storyline",
    qs1Desc: "Start the book. Give it a title, pick a forum, then add Scene 1 in Writer Studio.",

    qs2Title: "Open Proposals",
    qs2Desc: "When you want the crowd to help, open a proposal round and set a Next Scene Intent.",

    qs3Title: "Read, Comment, Build",
    qs3Desc: "Readers comment. Writers decide. Every decision is recorded. That’s how the story stays honest.",

    qsStartWriting: "Start Writing",
    qsExploreForums: "Explore Forums",
    qsOpenWriterStudio: "Open Writer Studio",
    qsBrowseStories: "Browse Stories",
    qsBrowse: "Browse",

    trendingStorylinesTitle: "Trending Storylines",
    trendingStorylinesDesc: "Storylines rise here when recent writing, proposals, feedback, or reader engagement gives them real momentum.",
    trendingActiveToday: "Active today",
    trendingActiveWeek: "Active this week",
    trendingActiveRecent: "Active recently",
    lastActiveLabel: "Last active",
    byLabel: "By",
    unclassifiedForum: "Unclassified Forum",
    noCoverStoryline: "No cover yet",
    liveFeedbackTitle: "Live Feedback",
    refreshShort: "Refresh",
    browseShort: "Browse",
    exploreShort: "Explore",
    updatedLabel: "Updated:",
    showingLabel: "Showing",
    ofLabel: "of",
    chooseStorylinePrompt: "Choose a storyline, or leave General Feedback.",
    generalFeedbackLabel: "General Feedback (NextScenes)",
    searchAllShort: "Search All...",
    shareThoughtPlaceholder: "Share a thought...",
    sortNewest: "Sort: Newest",
    sortOldest: "Sort: Oldest",
    loadMore: "Load more",
    openShort: "Open",
    discussShort: "Discuss",
    generalLabel: "General",
    storylineLabel: "Storyline",
    betaFeedbackTitle: "Beta Feedback",
    sendShort: "Send",
    refreshingTrending: "Refreshing trending…",
    noTrendingYet: "No trending storylines yet. Create one in Story Hub, then return here.",
    recentStorylinesShort: "Recent storylines",
    trendingStorylinesShort: "Trending storylines",
    refreshingShortLabel: "Refreshing…",
    sortPrefix: "Sort:",
    newestShort: "Newest",
    oldestShort: "Oldest",
    noFeedbackYet: "No feedback yet. Be the first to drop a comment. It will sync across devices.",
    readMoreShort: "Read more",
    showLessShort: "Show less",

    fourWaysTitle: "Four Ways to Write on NextScenes",

    fw1Title: "Collaborative Writing",
    fw2Title: "Group Writing (Open)",
    fw3Title: "Group Writing (Closed)",
    fw4Title: "Solo Writing",

    fw1Desc: "One writer starts a storyline. Others propose scenes. The community votes.",
    fw2Desc: "Anyone can contribute. Ideal for classrooms and public communities.",
    fw3Desc: "A private circle. Approved writers only. Professional control.",
    fw4Desc: "Write alone, publish live, and let readers follow your journey.",

    spotlightTitle: "Home Spotlight",
    spotlightSubtitle: "Writer · Book · Daily Words",
    refresh: "Refresh",

    writerOfMonth: "Writer of the Month",
    bookOfMonth: "Book of the Month",
    dailyWords: "Daily Words",

    readMore: "Read More →",
    view: "View →",
    noImageYet: "No image (set it in Admin Home Spotlight).",
    noCoverYet: "No cover yet (set it in Admin Home Spotlight).",
    quietMinds: "Quiet minds write better books.",

    tourTitle: "Guided Tour",
    tourSubtitle: "A short map so nobody gets lost.",
    tourHeader: "How NextScenes Works",

    tour1: "1) Story Hub / Start Writing: Create a storyline. This is the book’s spine.",
    tour2: "2) Writer Studio: Add canon scenes, open proposal rounds, approve the next canon step.",
    tour3: "3) Proposals + Voting: Readers submit proposals, vote, discuss.",
    tour4: "4) Decision Note: When the writer approves a proposal, they explain why.",

    close: "Close",

    ruleOfThumb: "Rule of thumb: keep WriterStudio stable. Improve everything around it.",

    startHereStep1Title: "Explore stories",
    startHereStep1Desc: "Browse stories in progress and see what others are building.",
    startHereStep1Action: "Explore stories",
    startHereStep2Title: "Open and read",
    startHereStep2Desc: "Read first, understand the journey, then begin your own when ready.",
    startHereStep2Action: "Start writing",
    startHereStep3Title: "Continue or propose",
    startHereStep3Desc: "Add your voice carefully through Writer Studio and build forward.",
    startHereStep3Action: "Open Writer Studio",
    aiPrinciplesTitle: "Human creativity first. AI assistance second. Author decision final.",
    aiPrinciplesDesc: "NextScenes uses AI to support writers, not to replace the human journey of writing.",
    aiPrinciplesLink: "Read our AI principles",

    heroTitleNew: "Stories should grow with purpose.",
    heroDescNew: "Explore creative work in development, share thoughtful feedback, or begin your own project. NextScenes keeps imagination active, disciplined, and human.",
    heroOriginNew: "Built for creators and thoughtful readers, from first idea to finished work.",
    exploreStories: "Explore Stories",
    startWritingReady: "Start Writing (when ready)",
  },

  fr: {
    heroTitle: "Une narration collaborative de haut niveau pour les lecteurs et les écrivains.",
    heroDesc1: "L’imagination avec conscience. Les histoires ici ne glorifient pas le chaos. Elles divertissent, mais elles reflètent aussi la vérité, la justice, la responsabilité et les conséquences des choix.",
    heroDesc2: "Commencez une histoire, invitez des scènes, et transformez plusieurs imaginations en un livre cohérent tout en gardant le contrôle de vos valeurs et de votre vision.",
    startWriting: "Commencer à écrire",
    browseStories: "Explorer les histoires",
    exploreForums: "Explorer les forums",

    startHereTitle: "Nouveau sur NextScenes ? Commencez ici en 30 secondes.",
    startHereDesc: "Commencez ici. Vous comprendrez la plateforme en l’utilisant, pas en l’étudiant.",
    step1: "Explorez les histoires dans Storylines",
    step2: "Ouvrez-en une et lisez ce que les autres ont écrit",
    step3: "Continuez l’histoire ou proposez votre propre scène",
    startHereNote: "Vous ne pouvez rien casser. Explorez simplement et voyez ce qui se passe.",
    startHereBtn: "Explorer les histoires dans Storylines",

    betaHelpTitle: "Aidez-nous à améliorer NextScenes (Bêta)",
    betaHelpDesc: "Qu’est-ce qui vous a semblé confus ? Qu’est-ce qui a bien fonctionné ? Votre voix aide à façonner cette plateforme.",
    shareFeedback: "Partager un retour",

    quickStartTitle: "Démarrage rapide",
    quickStartDesc1: "Nouveau ici ? Suivez ce chemin. Ce qui est puissant ne doit pas ressembler à un labyrinthe.",
    quickStartDesc2: "Vous n’avez pas besoin de tout maîtriser. Faites simplement une première action.",
    guidedTour: "Visite guidée",
    hide: "Masquer",

    qs1Title: "Créer une histoire",
    qs1Desc: "Commencez le livre. Donnez-lui un titre, choisissez un forum, puis ajoutez la Scène 1 dans Writer Studio.",

    qs2Title: "Ouvrir les propositions",
    qs2Desc: "Lorsque vous voulez que la communauté participe, ouvrez une phase de propositions et définissez une intention pour la prochaine scène.",

    qs3Title: "Lire, commenter, construire",
    qs3Desc: "Les lecteurs commentent. Les auteurs décident. Chaque décision est enregistrée. C’est ainsi que l’histoire reste honnête.",

    qsStartWriting: "Commencer à écrire",
    qsExploreForums: "Explorer les forums",
    qsOpenWriterStudio: "Ouvrir Writer Studio",
    qsBrowseStories: "Explorer les histoires",
    qsBrowse: "Explorer",

    trendingStorylinesTitle: "Tendances des histoires",
    trendingStorylinesDesc: "Les histoires montent ici lorsque l’écriture récente, les propositions, les retours ou l’engagement des lecteurs leur donnent un vrai élan.",
    trendingActiveToday: "Active aujourd’hui",
    trendingActiveWeek: "Active cette semaine",
    trendingActiveRecent: "Active récemment",
    lastActiveLabel: "Dernière activité",
    byLabel: "Par",
    unclassifiedForum: "Forum non classé",
    noCoverStoryline: "Aucune couverture",
    liveFeedbackTitle: "Retours en direct",
    refreshShort: "Actualiser",
    browseShort: "Parcourir",
    exploreShort: "Explorer",
    updatedLabel: "Mis à jour :",
    showingLabel: "Affichage",
    ofLabel: "sur",
    chooseStorylinePrompt: "Choisissez une histoire, ou laissez un retour général.",
    generalFeedbackLabel: "Retour général (NextScenes)",
    searchAllShort: "Tout rechercher...",
    shareThoughtPlaceholder: "Partagez une pensée...",
    sortNewest: "Trier : plus récent",
    sortOldest: "Trier : plus ancien",
    loadMore: "Charger plus",
    openShort: "Ouvrir",
    discussShort: "Discuter",
    generalLabel: "Général",
    storylineLabel: "Histoire",
    betaFeedbackTitle: "Retour bêta",
    sendShort: "Envoyer",
    refreshingTrending: "Actualisation des tendances…",
    noTrendingYet: "Aucune histoire en tendance pour le moment. Créez-en une dans Story Hub, puis revenez ici.",
    recentStorylinesShort: "Histoires récentes",
    trendingStorylinesShort: "Histoires en tendance",
    refreshingShortLabel: "Actualisation…",
    sortPrefix: "Trier :",
    newestShort: "Plus récent",
    oldestShort: "Plus ancien",
    noFeedbackYet: "Aucun retour pour le moment. Soyez le premier à en laisser un. Il sera synchronisé sur tous les appareils.",
    readMoreShort: "Lire plus",
    showLessShort: "Voir moins",

    fourWaysTitle: "Quatre façons d’écrire sur NextScenes",

    fw1Title: "Écriture collaborative",
    fw2Title: "Écriture de groupe (ouverte)",
    fw3Title: "Écriture de groupe (fermée)",
    fw4Title: "Écriture en solo",

    fw1Desc: "Un auteur commence une histoire. Les autres proposent des scènes. La communauté vote.",
    fw2Desc: "Tout le monde peut contribuer. Idéal pour les classes et les communautés publiques.",
    fw3Desc: "Un cercle privé. Auteurs approuvés uniquement. Contrôle professionnel.",
    fw4Desc: "Écrivez seul, publiez en direct et laissez les lecteurs suivre votre parcours.",

    spotlightTitle: "À la une",
    spotlightSubtitle: "Auteur · Livre · Paroles du jour",
    refresh: "Actualiser",

    writerOfMonth: "Auteur du mois",
    bookOfMonth: "Livre du mois",
    dailyWords: "Paroles du jour",

    readMore: "Lire plus →",
    view: "Voir →",
    noImageYet: "Pas d’image pour le moment (à définir dans Admin Home Spotlight).",
    noCoverYet: "Pas encore de couverture (à définir dans Admin Home Spotlight).",
    quietMinds: "Les esprits calmes écrivent de meilleurs livres.",

    tourTitle: "Visite guidée",
    tourSubtitle: "Un court guide pour que personne ne se perde.",
    tourHeader: "Comment fonctionne NextScenes",

    tour1: "1) Story Hub / Commencer à écrire : Créez une histoire. C’est la colonne vertébrale du livre.",
    tour2: "2) Writer Studio : Ajoutez des scènes canon, ouvrez des phases de propositions, validez la prochaine étape canon.",
    tour3: "3) Propositions + vote : Les lecteurs proposent, votent et discutent.",
    tour4: "4) Note de décision : Lorsque l’auteur valide une proposition, il explique pourquoi.",

    close: "Fermer",

    ruleOfThumb: "Règle générale : gardez WriterStudio stable. Améliorez tout ce qui l’entoure.",

    startHereStep1Title: "Explorer les histoires",
    startHereStep1Desc: "Parcourez les histoires en création et voyez ce que les autres construisent.",
    startHereStep1Action: "Explorer les histoires",
    startHereStep2Title: "Ouvrir et lire",
    startHereStep2Desc: "Lisez d’abord, comprenez le parcours, puis commencez le vôtre quand vous êtes prêt.",
    startHereStep2Action: "Commencer à écrire",
    startHereStep3Title: "Continuer ou proposer",
    startHereStep3Desc: "Ajoutez votre voix avec soin dans Writer Studio et faites avancer l’histoire.",
    startHereStep3Action: "Ouvrir Writer Studio",
    aiPrinciplesTitle: "La créativité humaine d’abord. L’assistance de l’IA ensuite. La décision finale à l’auteur.",
    aiPrinciplesDesc: "NextScenes utilise l’IA pour soutenir les auteurs, non pour remplacer le parcours humain de l’écriture.",
    aiPrinciplesLink: "Lire nos principes sur l’IA",

    heroTitleNew: "Les histoires doivent grandir avec du sens.",
    heroDescNew: "Explorez des œuvres créatives en développement, partagez des retours réfléchis ou commencez votre propre projet. NextScenes garde l’imagination vivante, disciplinée et humaine.",
    heroOriginNew: "Conçu pour les créateurs et les lecteurs attentifs, de la première idée à l’œuvre achevée.",
    exploreStories: "Explorer les histoires",
    startWritingReady: "Commencer à écrire (quand vous êtes prêt)",
  },
};

/* -------------------- helpers -------------------- */

function safeJsonParse(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function commentsKey(storyId) {
  return `ns.comments.${String(storyId || "")}`;
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

function countReplies(comments) {
  if (!Array.isArray(comments)) return 0;
  let n = 0;
  comments.forEach((c) => {
    const replies = Array.isArray(c?.replies) ? c.replies : [];
    n += replies.length;
  });
  return n;
}

function loadCommentsForStory(storyId) {
  if (!storyId) return [];
  try {
    const raw = window.localStorage.getItem(commentsKey(storyId));
    if (!raw) return [];
    const parsed = safeJsonParse(raw, []);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function stableStringify(obj) {
  try {
    return JSON.stringify(obj);
  } catch {
    return String(Date.now());
  }
}

function startOfLocalDayTs() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function readBoolStorage(key, fallback = false) {
  try {
    const v = window.localStorage.getItem(key);
    if (v === "true") return true;
    if (v === "false") return false;
    return fallback;
  } catch {
    return fallback;
  }
}

function writeBoolStorage(key, value) {
  try {
    window.localStorage.setItem(key, value ? "true" : "false");
  } catch {
    // ignore
  }
}

function safeText(v) {
  return String(v || "").trim();
}

function splitDailyWordsText(value, lang = "en") {
  const raw = safeText(value);
  if (!raw) return { english: "", french: "" };

  const normalized = raw
    .replace(/\*{5,}/g, "\n---NS_DAILY_WORDS_DIVIDER---\n")
    .replace(/\s+---NS_DAILY_WORDS_DIVIDER---\s+/g, "\n---NS_DAILY_WORDS_DIVIDER---\n")
    .trim();

  const parts = normalized
    .split("---NS_DAILY_WORDS_DIVIDER---")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    return {
      english: parts[0],
      french: parts.slice(1).join("\n\n"),
    };
  }

  return lang === "fr" ? { english: "", french: raw } : { english: raw, french: "" };
}

function localizedText(lang, enValue, frValue) {
  const en = safeText(enValue);
  const fr = safeText(frValue);
  return lang === "fr" ? fr || en : en || fr;
}

function splitParagraphs(value) {
  const text = safeText(value);
  if (!text) return [];
  return text
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function splitSentencesForPreview(value) {
  const text = normalizeSpaces(value);
  if (!text) return [];

  const matches = text.match(/[^.!?]+[.!?]+[\)\]\"’”']*|[^.!?]+$/g);
  return Array.isArray(matches)
    ? matches.map((part) => normalizeSpaces(part)).filter(Boolean)
    : [text];
}

function buildSpotlightTextParts(primaryText, fallbackText, sentenceCount = 2) {
  const fullText = safeText(primaryText) || safeText(fallbackText);
  if (!fullText) return { teaser: "", remainder: "" };

  const compactFull = normalizeSpaces(fullText);
  const sentences = splitSentencesForPreview(compactFull);

  if (sentences.length <= sentenceCount) {
    return { teaser: compactFull, remainder: "" };
  }

  const teaser = sentences.slice(0, sentenceCount).join(" " );
  const remainder = sentences.slice(sentenceCount).join(" " );

  return { teaser: teaser || compactFull, remainder };
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function safeLower(v) {
  return String(v || "").toLowerCase();
}

function normalizeSpaces(s) {
  return String(s || "").replace(/\s+/g, " ").trim();
}

function storyIdOf(s) {
  return String(s?._id || s?.id || "").trim();
}

function storyTitleOf(s) {
  return String(s?.title || "Untitled Storyline");
}

function storyForumOf(s) {
  return String(s?.forum || "");
}

function activityLabelForBand(band, t) {
  const value = safeText(band).toLowerCase();
  if (value === "today") return t.trendingActiveToday || "Active today";
  if (value === "week") return t.trendingActiveWeek || "Active this week";
  return t.trendingActiveRecent || "Active recently";
}

function parseTimeMs(v) {
  const t = new Date(v || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

function getCookie(name) {
  try {
    const n = `${name}=`;
    const parts = String(document.cookie || "").split(";");
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i].trim();
      if (p.startsWith(n)) return decodeURIComponent(p.slice(n.length));
    }
    return "";
  } catch {
    return "";
  }
}

async function apiGet(path) {
  if (api && typeof api.get === "function") return await api.get(path);
  const res = await fetch(path, { method: "GET", credentials: "include" });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const msg = String(data?.message || data?.error || "Request failed");
    const err = new Error(msg);
    err.data = data;
    throw err;
  }
  return data;
}

async function apiPost(path, body) {
  if (api && typeof api.post === "function") return await api.post(path, body);

  const csrf = getCookie("ns.csrf");
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(csrf ? { "X-CSRF-Token": csrf } : {}),
    },
    body: JSON.stringify(body || {}),
  });

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const msg = String(data?.message || data?.error || "Request failed");
    const err = new Error(msg);
    err.data = data;
    throw err;
  }
  return data;
}

function feedbackSourceLabel(item) {
  const src = safeLower(item?.source);
  if (src === "writerstudio" || src === "writer-studio" || src === "studio") {
    return "WriterStudio";
  }
  return "Home";
}

function feedbackSourceStyles(item) {
  const src = safeLower(item?.source);
  const isWs =
    src === "writerstudio" || src === "writer-studio" || src === "studio";

  const cardStyle = isWs
    ? {
        borderLeft: "4px solid rgba(34,197,94,0.70)",
        background: "rgba(34,197,94,0.06)",
      }
    : {
        borderLeft: "4px solid rgba(59,130,246,0.65)",
        background: "rgba(59,130,246,0.04)",
      };

  const badgeStyle = isWs
    ? {
        color: "rgba(6,95,70,1)",
        background: "rgba(34,197,94,0.14)",
        border: "1px solid rgba(34,197,94,0.35)",
      }
    : {
        color: "rgba(30,64,175,1)",
        background: "rgba(59,130,246,0.12)",
        border: "1px solid rgba(59,130,246,0.30)",
      };

  return { isWs, cardStyle, badgeStyle };
}

function ModalShell({
  title,
  subtitle,
  onClose,
  children,
  maxWidth = 980,
  zIndex = 1600,
  closeLabel = "Close",
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex,
        padding: 16,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose && onClose();
      }}
    >
      <div
        className="card"
        style={{
          width: "100%",
          maxWidth,
          maxHeight: "86vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "12px 14px",
            borderBottom: "1px solid #e5e7eb",
            background: "#f8fafc",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0 }}>{title}</h2>
            {subtitle ? (
              <p style={{ marginTop: 6, marginBottom: 0, color: "#475569" }}>
                {subtitle}
              </p>
            ) : null}
          </div>

          <button
            type="button"
            className="ns-button ns-button-outline"
            onClick={onClose}
          >
            {closeLabel}
          </button>
        </div>

        <div style={{ padding: 14, overflowY: "auto" }}>{children}</div>
      </div>
    </div>
  );
}

function SpotlightImage({
  src,
  alt,
  onError,
  variant = "writer",
}) {
  if (!src) return null;

  const imageVariant = variant === "book" ? "book" : "writer";

  return (
    <div
      className={`ns-home-spotlight-image-frame ns-home-spotlight-image-${imageVariant}`}
    >
      <img
        className="ns-home-spotlight-image-backdrop"
        src={src}
        alt=""
        aria-hidden="true"
        onError={onError}
      />

      <div className="ns-home-spotlight-image-overlay" aria-hidden="true" />

      <img
        className="ns-home-spotlight-image-main"
        src={src}
        alt={alt}
        onError={onError}
      />
    </div>
  );
}

function normalizeTrendingPayload(res) {
  const data = res?.data ? res.data : res;
  const items = Array.isArray(data?.items) ? data.items : [];

  return items
    .map((t) => {
      const id = String(t?.id || t?._id || "").trim();
      if (!id) return null;

      const lastActiveIso = String(t?.lastActiveAt || t?.updatedAt || "").trim();
      const updatedAt = String(t?.updatedAt || lastActiveIso || "").trim();
      const coverUrl = safeText(
        t?.coverUrl ||
          t?.coverImageUrl ||
          t?.publicationCoverUrl ||
          t?.imageUrl ||
          ""
      );

      return {
        id,
        title: safeText(t?.title) || "Untitled Storyline",
        forum: safeText(t?.forum),
        author: safeText(t?.author),
        writingMode: safeText(t?.writingMode),
        description: safeText(t?.description || t?.synopsis || t?.teaser),
        coverUrl,
        updatedAt,
        lastActiveAt: lastActiveIso ? new Date(lastActiveIso).getTime() : 0,
        lastActiveIso,
        activityBand: safeText(t?.activityBand) || "recent",
      };
    })
    .filter(Boolean)
    .slice(0, 3);
}

function getInitials(name) {
  const s = String(name || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  const first = parts[0] ? parts[0][0] : "?";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

function AvatarCircle({ name, avatarUrl, size = 32 }) {
  const initials = getInitials(name);
  const dim = Math.max(22, Number(size) || 32);

  const wrapStyle = {
    width: dim,
    height: dim,
    borderRadius: "50%",
    overflow: "hidden",
    background: "#e8f5ee",
    border: "1px solid rgba(22, 163, 74, 0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    boxShadow: "0 1px 0 rgba(15, 23, 42, 0.04)",
  };

  const textStyle = {
    fontSize: Math.max(11, Math.round(dim * 0.38)),
    fontWeight: 800,
    color: "#14532d",
    letterSpacing: "0.5px",
    lineHeight: 1,
  };

  if (avatarUrl) {
    return (
      <div style={wrapStyle} aria-label={name ? `Avatar: ${name}` : "Avatar"}>
        <img
          src={avatarUrl}
          alt={name ? `${name} avatar` : "Avatar"}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <span style={textStyle}>{initials}</span>
      </div>
    );
  }

  return (
    <div style={wrapStyle} aria-label={name ? `Avatar: ${name}` : "Avatar"}>
      <span style={textStyle}>{initials}</span>
    </div>
  );
}

export default function Home({
  onNavigate,
  onNavigatePage,
  onStartWriting,
  onBrowseStories,
  onExploreForums,
  onMystery250,
  onOpenWriterStudio,
}) {
  const lang =
    (typeof window !== "undefined" &&
      window.localStorage.getItem("ns.lang")) ||
    "en";
  const t = TEXT[lang] || TEXT.en;

  const userCtx = typeof useUser === "function" ? useUser() : null;
  const currentUser = userCtx?.user || userCtx?.currentUser || null;

  const isLoggedIn =
    !!currentUser?.id ||
    !!currentUser?._id ||
    !!currentUser?.email ||
    !!currentUser?.username;
  const canPostDiscussion = Boolean(isLoggedIn && ["writer","power","moderator","admin"].includes(String(currentUser?.role||"").toLowerCase().trim()));

  const [stories, setStories] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const ONBOARD_DISMISS_KEY = "ns.onboarding.dismissed";
  const [onboardingDismissed, setOnboardingDismissed] = useState(() =>
    readBoolStorage(ONBOARD_DISMISS_KEY, false)
  );
  const [showOnboardingTour, setShowOnboardingTour] = useState(false);

  const SPOTLIGHT_CACHE_KEY = "ns.home.spotlight.cache";
  const LIVEFB_PING_KEY = "ns.liveFeedback.ping";
  const GENERAL_FEEDBACK_ID = "__general__";

  const [spotlight, setSpotlight] = useState(() => {
    try {
      const raw = window.localStorage.getItem(SPOTLIGHT_CACHE_KEY);
      const parsed = safeJsonParse(raw, null);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  });
  const [spotErr, setSpotErr] = useState("");

  const [trendingItems, setTrendingItems] = useState([]);
  const [trendBusy, setTrendBusy] = useState(false);
  const [trendErr, setTrendErr] = useState("");

  const [liveFeedback, setLiveFeedback] = useState([]);
  const [liveFbBusy, setLiveFbBusy] = useState(false);
  const [liveFbErr, setLiveFbErr] = useState("");

  const [liveFbSort, setLiveFbSort] = useState("newest");
  const [liveFbLimit, setLiveFbLimit] = useState(20);
  const [expandedLiveFb, setExpandedLiveFb] = useState(() => ({}));

  const [fbStoryId, setFbStoryId] = useState(GENERAL_FEEDBACK_ID);
  const [fbText, setFbText] = useState("");
  const [fbSentMsg, setFbSentMsg] = useState("");

  const BETA_SEEN_KEY = "ns.betaFeedback.seenReplies.v1";
  const [showBetaFeedbackModal, setShowBetaFeedbackModal] = useState(false);
  const [betaItems, setBetaItems] = useState([]);
  const [betaBusy, setBetaBusy] = useState(false);
  const [betaErr, setBetaErr] = useState("");
  const [betaDraft, setBetaDraft] = useState("");
  const [betaSendMsg, setBetaSendMsg] = useState("");
  const [openBetaThreadId, setOpenBetaThreadId] = useState("");
  const [betaNewReplyCount, setBetaNewReplyCount] = useState(0);
  const [betaToast, setBetaToast] = useState({ show: false, msg: "" });
  const [betaSeenMap, setBetaSeenMap] = useState(() => {
    try {
      const raw = window.localStorage.getItem(BETA_SEEN_KEY);
      const parsed = safeJsonParse(raw, {});
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  });

  const [showStoryPicker, setShowStoryPicker] = useState(false);
  const [storySearch, setStorySearch] = useState("");

  const [writerImgOk, setWriterImgOk] = useState(true);
  const [bookImgOk, setBookImgOk] = useState(true);

  function go(page) {
    if (typeof onNavigate === "function") return onNavigate(page);
    if (typeof onNavigatePage === "function") return onNavigatePage(page);
  }

  function goStartWriting() {
    if (typeof onStartWriting === "function") return onStartWriting();
    go("storyHub");
  }
  function goBrowseStories() {
    if (typeof onBrowseStories === "function") return onBrowseStories();
    go("storylines");
  }
  function goExploreForums() {
    if (typeof onExploreForums === "function") return onExploreForums();
    go("forums");
  }
  function goWriterStudio() {
    if (typeof onOpenWriterStudio === "function") return onOpenWriterStudio();
    go("writerStudio");
  }
  function goMystery250() {
    if (typeof onMystery250 === "function") return onMystery250();
    go("mystery250");
  }

  const defaultSpotlight = useMemo(() => {
    return {
      writer: {
        name: "Chimamanda Ngozi Adichie",
        monthLabel: "Writer of the Month",
        imageUrl: "/assets/adichie.jpg",
        teaser:
          "A voice that confronts truth with clarity, exploring identity, history, and the quiet strength of ordinary lives.",
        fullText:
          "Chimamanda Ngozi Adichie stands as one of the most compelling literary voices of our time. Her work does not chase applause. It questions, reveals, and invites reflection. Through her stories, she brings history and human experience into sharp, unforgettable focus.",
        works: ["Half of a Yellow Sun"],
      },

      book: {
        title: "Half of a Yellow Sun",
        author: "Chimamanda Ngozi Adichie",
        coverUrl: "/assets/half-yellow-sun.jpg",
        teaser:
          "A powerful story set during the Biafran War, where love, loyalty, and survival are tested.",
        fullText:
          "Set during one of the most turbulent periods in Nigerian history, this novel follows the lives of individuals caught in the reality of war. It is intimate, human, and deeply moving, revealing not just events, but their impact on the soul.",
        linkUrl: "",
      },

      words: {
        title: "Daily Words",
        text: "Great stories do not entertain alone. They awaken.",
        author: "NextScenes",
      },
    };
  }, []);

  const spotlightView = useMemo(() => {
    const sp = spotlight || defaultSpotlight;
    const w = sp?.writer || {};
    const b = sp?.book || {};
    const dw = sp?.words || sp?.daily || {};

    const writer = {
      name: safeText(w.name) || defaultSpotlight.writer.name,
      monthLabel: safeText(w.monthLabel) || defaultSpotlight.writer.monthLabel,
      imageUrl:
        safeText(w.imageUrl) ||
        safeText(w.imageSrc) ||
        defaultSpotlight.writer.imageUrl ||
        "",
      teaser: localizedText(lang, w.teaser, w.teaserFr) || defaultSpotlight.writer.teaser,
      fullText: localizedText(lang, w.fullText, w.fullTextFr) || "",
      works: safeArray(w.works),
    };

    const book = {
      title: localizedText(lang, b.title, b.titleFr) || defaultSpotlight.book.title,
      author: safeText(b.author) || defaultSpotlight.book.author,
      coverUrl: localizedText(lang, b.coverUrl, b.coverUrlFr) || "",
      teaser: localizedText(lang, b.teaser, b.teaserFr) || defaultSpotlight.book.teaser,
      fullText: localizedText(lang, b.fullText, b.fullTextFr) || "",
      linkUrl: safeText(b.linkUrl) || "",
    };

    const words = {
      title: safeText(dw.title) || defaultSpotlight.words.title,
      text:
        localizedText(lang, dw.text || dw.quote, dw.textFr || dw.quoteFr) ||
        defaultSpotlight.words.text,
      author: safeText(dw.author) || defaultSpotlight.words.author,
      dateLabel: safeText(dw.dateLabel || dw.date) || "",
    };

    return { writer, book, words };
  }, [spotlight, defaultSpotlight, lang]);

  const isMountedRef = useRef(true);
  const inFlightStoriesRef = useRef(false);
  const inFlightLiveFbRef = useRef(false);
  const inFlightTrendRef = useRef(false);
  const fingerprintRef = useRef("");

  async function reloadStories() {
    if (inFlightStoriesRef.current) return;
    inFlightStoriesRef.current = true;

    try {
      const stored = loadStoredStories() || [];
      let remote = [];
      try {
        const res = await api.getStories();
        remote = Array.isArray(res) ? res : res?.stories || [];
        if (!Array.isArray(remote)) remote = [];
      } catch {
        remote = [];
      }

      const merged = mergeStoredAndRemoteStories(stored, remote);
      saveStoredStories(merged);
      const arr = Array.isArray(merged) ? merged : [];

      const fp = stableStringify(
        arr.map((s) => ({
          id: s?._id || s?.id,
          title: s?.title,
          forum: s?.forum,
          scenesLen: Array.isArray(s?.scenes) ? s.scenes.length : 0,
          updatedAt: s?.updatedAt || s?.lastUpdatedAt || s?.createdAt || "",
        }))
      );

      if (!isMountedRef.current) return;

      if (fp !== fingerprintRef.current) {
        fingerprintRef.current = fp;
        setStories(arr);
      }

      setRefreshTick((t) => t + 1);
      setLastUpdatedAt(new Date());
    } finally {
      inFlightStoriesRef.current = false;
    }
  }

  async function reloadSpotlight() {
    try {
      const res = await apiGet(`/api/home/spotlight?t=${Date.now()}`);
      const sp = res?.spotlight || null;

      if (!isMountedRef.current) return;

      if (sp && typeof sp === "object") {
        setSpotlight(sp);
        try {
          window.localStorage.setItem(SPOTLIGHT_CACHE_KEY, JSON.stringify(sp));
        } catch {
          // ignore
        }
      }

      setSpotErr("");
      setWriterImgOk(true);
      setBookImgOk(true);
    } catch (e) {
      if (!isMountedRef.current) return;
      setSpotErr(String(e?.message || "Failed to load spotlight."));
    }
  }

  async function reloadTrending(showBusy = false) {
    if (inFlightTrendRef.current) return;
    inFlightTrendRef.current = true;

    try {
      if (showBusy) setTrendBusy(true);
      setTrendErr("");

      const res = await apiGet(`/api/home/trending?t=${Date.now()}`);
      const normalized = normalizeTrendingPayload(res);

      if (!isMountedRef.current) return;
      setTrendingItems(normalized);
    } catch (e) {
      if (!isMountedRef.current) return;
      setTrendErr(String(e?.message || "Failed to load trending."));
      setTrendingItems([]);
    } finally {
      inFlightTrendRef.current = false;
      if (isMountedRef.current && showBusy) setTrendBusy(false);
    }
  }

  async function reloadLiveFeedback(force = false, showBusy = false) {
    if (inFlightLiveFbRef.current && !force) return;
    inFlightLiveFbRef.current = true;

    try {
      if (showBusy) setLiveFbBusy(true);
      setLiveFbErr("");
      const res = await apiGet(`/api/feedback/live?t=${Date.now()}`);
      const data = res?.data ? res.data : res;
      const items = Array.isArray(data?.items) ? data.items : [];
      if (!isMountedRef.current) return;
      setLiveFeedback(items);
    } catch (e) {
      if (!isMountedRef.current) return;
      setLiveFbErr(String(e?.message || "Failed to load live feedback."));
    } finally {
      inFlightLiveFbRef.current = false;
      if (isMountedRef.current && showBusy) setLiveFbBusy(false);
    }
  }

  async function submitLiveFeedback(e) {
    try {
      if (e && typeof e.preventDefault === "function") e.preventDefault();
      setFbSentMsg("");

      if (!canPostDiscussion) {
        setFbSentMsg("Only authorised writers can post feedback. Readers have read-only access.");
        return;
      }

      const chosen = String(fbStoryId || "").trim();
      const textIn = String(fbText || "").trim();

      if (!textIn) {
        setFbSentMsg("Please write your feedback first.");
        return;
      }

      const isGeneral = chosen === GENERAL_FEEDBACK_ID || !chosen;
      let story = null;
      if (!isGeneral) {
        story =
          stories.find((s) => String(s?._id || s?.id || "") === chosen) || null;
        if (!story) {
          setFbSentMsg("Please select a valid storyline.");
          return;
        }
      }

      await apiPost("/api/feedback", {
        storyId: isGeneral ? "" : chosen,
        storyTitle: isGeneral ? "General Feedback" : story?.title || "",
        forum: isGeneral ? "" : story?.forum || "",
        text: textIn,
        source: "home",
        target: isGeneral ? "general" : "storyline",
      });

      if (!isMountedRef.current) return;

      setFbText("");
      setFbSentMsg("Sent. Thank you.");

      try {
        window.localStorage.setItem(LIVEFB_PING_KEY, String(Date.now()));
      } catch {
        // ignore
      }

      setLiveFbLimit(20);
      reloadLiveFeedback(true, true);
      if (isLoggedIn && !isAdminLike()) {
        reloadBetaFeedback(false);
      }
    } catch (ex) {
      if (!isMountedRef.current) return;
      setFbSentMsg(String(ex?.message || "Failed to send feedback."));
    }
  }

  function currentUserId() {
    return String(currentUser?.id || currentUser?._id || "").trim();
  }

  function isAdminLike() {
    const r = safeLower(currentUser?.role);
    return r === "admin";
  }

  function safeNum(v, fallback = 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  }

  function timeMs(v) {
    const t = new Date(v || 0).getTime();
    return Number.isFinite(t) ? t : 0;
  }

  function sortOldestFirst(list) {
    const arr = Array.isArray(list) ? list.slice() : [];
    arr.sort((a, b) => timeMs(a?.createdAt) - timeMs(b?.createdAt));
    return arr;
  }

  function readSeenMap() {
    try {
      const raw = window.localStorage.getItem(BETA_SEEN_KEY);
      const parsed = safeJsonParse(raw, {});
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  function writeSeenMap(map) {
    try {
      window.localStorage.setItem(BETA_SEEN_KEY, JSON.stringify(map || {}));
    } catch {
      // ignore
    }
  }

  async function reloadBetaFeedback(showBusy = false, opts = {}) {
    const uid = currentUserId();
    if (!uid) return [];

    try {
      if (showBusy) setBetaBusy(true);
      setBetaErr("");

      const res = await apiGet(
        `/api/feedback/live?target=beta&limit=100&t=${Date.now()}`
      );
      const data = res?.data ? res.data : res;
      const items = Array.isArray(data?.items) ? data.items : [];
      if (!isMountedRef.current) return items;

      setBetaItems(items);

      const shouldMarkSeen = !!opts.markSeen || showBetaFeedbackModal;
      if (shouldMarkSeen) {
        const { openedThreadId } = markAllBetaRepliesSeen(items);
        if (openedThreadId) setOpenBetaThreadId(openedThreadId);
      }

      return items;
    } catch (e) {
      if (!isMountedRef.current) return [];
      setBetaErr(String(e?.message || "Failed to load beta feedback."));
      return [];
    } finally {
      if (isMountedRef.current && showBusy) setBetaBusy(false);
    }
  }

  function computeUserBetaThreads(items) {
    const uid = currentUserId();
    const flat = Array.isArray(items) ? items : [];
    const top = [];
    const replies = {};

    for (const it of flat) {
      const pid = String(it?.parentId || "").trim();
      const id = String(it?.id || "").trim();
      const authorId = String(it?.authorId || "").trim();

      if (!id) continue;

      if (pid) {
        if (!replies[pid]) replies[pid] = [];
        replies[pid].push(it);
      } else {
        if (authorId && authorId === uid) top.push(it);
      }
    }

    top.sort((a, b) => timeMs(b?.createdAt) - timeMs(a?.createdAt));
    Object.keys(replies).forEach((k) => {
      replies[k] = sortOldestFirst(replies[k]);
    });

    return { top, replies };
  }

  function markBetaThreadSeen(parentId, repliesForThread) {
    const pid = String(parentId || "").trim();
    if (!pid) return;

    const arr = Array.isArray(repliesForThread) ? repliesForThread : [];
    const latestMs = arr.reduce((acc, r) => Math.max(acc, timeMs(r?.createdAt)), 0);

    const stored = readSeenMap();
    const next = { ...(stored || {}) };
    next[pid] = latestMs || Date.now();

    setBetaSeenMap(next);
    writeSeenMap(next);
  }

  async function sendBetaFeedback(e) {
    try {
      if (e && typeof e.preventDefault === "function") e.preventDefault();
      setBetaSendMsg("");

      if (!isLoggedIn) {
        setBetaSendMsg("Please log in to send beta feedback.");
        return;
      }

      const textIn = String(betaDraft || "").trim();
      if (!textIn) {
        setBetaSendMsg("Please write your beta feedback first.");
        return;
      }

      await apiPost("/api/feedback", {
        storyId: "",
        storyTitle: "Beta Feedback",
        forum: "",
        text: textIn,
        source: "home",
        target: "beta",
        parentId: "",
      });

      if (!isMountedRef.current) return;

      setBetaDraft("");
      setBetaSendMsg("Sent. Thank you.");
      reloadBetaFeedback(true);
    } catch (e) {
      if (!isMountedRef.current) return;
      setBetaSendMsg(String(e?.message || "Failed to send beta feedback."));
    }
  }

  function showBetaToast(msg) {
    setBetaToast({ show: true, msg: String(msg || "New admin reply.") });
    window.setTimeout(() => {
      if (!isMountedRef.current) return;
      setBetaToast({ show: false, msg: "" });
    }, 5500);
  }

  function markAllBetaRepliesSeen(items) {
    const uid = currentUserId();
    if (!uid) return { openedThreadId: "" };

    const { top, replies } = computeUserBetaThreads(items);
    const seen = readSeenMap();
    let openedThreadId = "";

    const sortedTop = [...top].sort((a, b) => timeMs(b?.createdAt) - timeMs(a?.createdAt));
    for (const t of sortedTop) {
      const pid = String(t?.id || "").trim();
      const arr = Array.isArray(replies[pid]) ? replies[pid] : [];
      if (!arr.length) continue;

      const lastReplyMs = arr.reduce((acc, r) => Math.max(acc, timeMs(r?.createdAt)), 0);
      if (lastReplyMs > 0) seen[pid] = lastReplyMs;
      if (!openedThreadId) openedThreadId = pid;
    }

    if (!seen.__lastToastMs) seen.__lastToastMs = 0;

    writeSeenMap(seen);
    setBetaSeenMap(seen);

    return { openedThreadId };
  }

  async function openBetaModalFromToast() {
    setBetaToast({ show: false, msg: "" });
    setShowBetaFeedbackModal(true);

    const items = await reloadBetaFeedback(true, { markSeen: true });
    const { openedThreadId } = markAllBetaRepliesSeen(items);
    if (openedThreadId) setOpenBetaThreadId(openedThreadId);
  }

  function updateBetaNotification(items) {
    const uid = currentUserId();
    if (!uid) return;

    const { top, replies } = computeUserBetaThreads(items);
    const seen = readSeenMap();

    let newCount = 0;
    let newestReplyMs = 0;

    for (const t of top) {
      const pid = String(t?.id || "").trim();
      const arr = Array.isArray(replies[pid]) ? replies[pid] : [];
      if (!arr.length) continue;

      const lastReplyMs = arr.reduce((acc, r) => Math.max(acc, timeMs(r?.createdAt)), 0);
      const lastSeenMs = safeNum(seen[pid], 0);

      if (lastReplyMs > lastSeenMs) {
        newCount += 1;
        newestReplyMs = Math.max(newestReplyMs, lastReplyMs);
      }
    }

    if (isMountedRef.current) setBetaNewReplyCount(newCount);

    if (!showBetaFeedbackModal && newCount > 0 && newestReplyMs > 0) {
      const now = Date.now();
      const stored = readSeenMap();
      const lastToastMs = safeNum(stored.__lastToastMs, 0);
      const lastToastReplyMs = safeNum(stored.__lastToastReplyMs, 0);

      if (newestReplyMs > lastToastReplyMs && now - lastToastMs > 3000) {
        const next = { ...(stored || {}) };
        next.__lastToastMs = now;
        next.__lastToastReplyMs = newestReplyMs;
        writeSeenMap(next);
        setBetaSeenMap(next);
        showBetaToast("Admin replied to your beta feedback.");
      }
    }
  }

  useEffect(() => {
    isMountedRef.current = true;

    reloadStories();
    reloadSpotlight();
    reloadTrending(true);
    reloadLiveFeedback(true, true);

    if (isLoggedIn && !isAdminLike()) {
      reloadBetaFeedback(false);
    }

    const intervalStories = window.setInterval(() => {
      reloadStories();
    }, 30000);

    const intervalSpot = window.setInterval(() => {
      reloadSpotlight();
    }, 30000);

    const intervalTrend = window.setInterval(() => {
      reloadTrending(false);
    }, 30000);

    const intervalLiveFb = window.setInterval(() => {
      reloadLiveFeedback();
    }, 15000);

    const intervalBeta = window.setInterval(async () => {
      if (!isLoggedIn || isAdminLike()) return;
      try {
        const res = await apiGet(
          `/api/feedback/live?target=beta&limit=100&t=${Date.now()}`
        );
        const data = res?.data ? res.data : res;
        const items = Array.isArray(data?.items) ? data.items : [];
        if (!isMountedRef.current) return;
        setBetaItems(items);
        updateBetaNotification(items);
      } catch {
        // silent
      }
    }, 20000);

    function onVisibility() {
      if (!document.hidden) {
            reloadStories();
        reloadSpotlight();
        reloadTrending(false);
        reloadLiveFeedback(true, true);
      }
    }
    document.addEventListener("visibilitychange", onVisibility);

    function onStorage(e) {
      if (!e) return;

      if (
        e.key === "ns.stories" ||
        e.key === "ns.remoteStories" ||
        (typeof e.key === "string" && e.key.startsWith("ns.comments."))
      ) {
        reloadStories();
      }

      if (e.key === SPOTLIGHT_CACHE_KEY) {
        try {
          const parsed = safeJsonParse(e.newValue, null);
          if (parsed && typeof parsed === "object") setSpotlight(parsed);
        } catch {
          // ignore
        }
      }

      if (e.key === LIVEFB_PING_KEY) {
        setLiveFbLimit(20);
        reloadLiveFeedback(true, false);
      }
    }
    window.addEventListener("storage", onStorage);

    return () => {
      isMountedRef.current = false;
      window.clearInterval(intervalStories);
      window.clearInterval(intervalSpot);
      window.clearInterval(intervalTrend);
      window.clearInterval(intervalLiveFb);
      window.clearInterval(intervalBeta);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ids = new Set(
      (Array.isArray(liveFeedback) ? liveFeedback : []).map((x, idx) => {
        const storyId = String(x?.storyId || "").trim();
        return `${storyId || "general"}_${x?.id || idx}`;
      })
    );

    setExpandedLiveFb((m) => {
      const next = { ...(m || {}) };
      let changed = false;
      Object.keys(next).forEach((k) => {
        if (!ids.has(k)) {
          delete next[k];
          changed = true;
        }
      });
      return changed ? next : m;
    });
  }, [liveFeedback]);

  function dismissOnboarding() {
    writeBoolStorage(ONBOARD_DISMISS_KEY, true);
    setOnboardingDismissed(true);
  }

  function openAndHighlightStory(story) {
    const id = story?._id || story?.id;
    if (!id) {
      goBrowseStories();
      return;
    }

    try {
      window.localStorage.setItem("ns.selectedStoryId", String(id));
      window.localStorage.setItem("ns.writerStudio.focusStoryId", String(id));
      if (story?.forum)
        window.localStorage.setItem("ns.selectedForum", String(story.forum));
    } catch {
      // ignore
    }

    goBrowseStories();
  }

  function openThreadInWriterStudio(story, commentId) {
    const id = story?._id || story?.id;
    if (!id) return;

    try {
      window.localStorage.setItem("ns.selectedStoryId", String(id));
      window.localStorage.setItem("ns.reader.focusStoryId", String(id));
      window.localStorage.setItem("ns.writerStudio.focusStoryId", String(id));

      if (story?.forum)
        window.localStorage.setItem("ns.selectedForum", String(story.forum));

      window.localStorage.setItem("ns.threadTargetStoryId", String(id));
      if (commentId)
        window.localStorage.setItem("ns.threadTargetCommentId", String(commentId));
      else window.localStorage.removeItem("ns.threadTargetCommentId");

      if (commentId)
        window.localStorage.setItem("ns.writerStudio.focusCommentId", String(commentId));
      else window.localStorage.removeItem("ns.writerStudio.focusCommentId");

      window.localStorage.removeItem("ns.focusStoryId");
      window.localStorage.removeItem("focusStoryId");
      window.localStorage.removeItem("ns.focusCommentId");
      window.localStorage.removeItem("focusCommentId");
    } catch {
      // ignore
    }

    goWriterStudio();
  }

  const recentStories = useMemo(() => {
    const arr = Array.isArray(stories) ? stories : [];
    const scored = arr
      .map((s) => {
        const scenes = Array.isArray(s?.scenes) ? s.scenes : [];
        const lastSceneTime = scenes.reduce((acc, x) => {
          const t = new Date(x?.createdAt || 0).getTime();
          return Number.isFinite(t) && t > acc ? t : acc;
        }, 0);

        const storyUpdated =
          parseTimeMs(s?.updatedAt) ||
          parseTimeMs(s?.lastUpdatedAt) ||
          parseTimeMs(s?.createdAt) ||
          0;

        const lastActive = Math.max(lastSceneTime, storyUpdated) || 0;
        return { s, id: storyIdOf(s), t: lastActive };
      })
      .filter((x) => x.id);

    scored.sort((a, b) => (b.t || 0) - (a.t || 0));
    return scored.slice(0, 12).map((x) => x.s);
  }, [stories]);

  const trendingStoriesForDropdown = useMemo(() => {
    return (Array.isArray(trendingItems) ? trendingItems : []).map((tItem) => ({
      _id: tItem.id,
      id: tItem.id,
      title: tItem.title,
      forum: tItem.forum,
      author: tItem.author,
      writingMode: tItem.writingMode,
    }));
  }, [trendingItems]);

  const storySearchResults = useMemo(() => {
    const q = normalizeSpaces(storySearch);
    const ql = safeLower(q);
    const arr = Array.isArray(stories) ? stories : [];
    if (!ql) {
      const copy = arr.slice();
      copy.sort((a, b) => storyTitleOf(a).localeCompare(storyTitleOf(b)));
      return copy.slice(0, 80);
    }

    const tokens = ql.split(" ").filter(Boolean);
    const filtered = arr.filter((s) => {
      const hay = safeLower(
        `${storyTitleOf(s)} ${storyForumOf(s)} ${safeText(s?.author || "")}`
      );
      return tokens.every((token) => hay.includes(token));
    });

    filtered.sort((a, b) => storyTitleOf(a).localeCompare(storyTitleOf(b)));
    return filtered.slice(0, 120);
  }, [stories, storySearch]);

  const betaThreads = useMemo(() => {
    return computeUserBetaThreads(betaItems);
  }, [betaItems, currentUser?.id, currentUser?._id]);

  const betaThreadList = betaThreads?.top || [];
  const betaRepliesByParent = betaThreads?.replies || {};

  useEffect(() => {
    if (!isLoggedIn || isAdminLike()) return;
    updateBetaNotification(betaItems);
  }, [betaItems, betaSeenMap, isLoggedIn]);

  const heartbeatText = useMemo(() => {
    if (!lastUpdatedAt) return "";
    return `${t.updatedLabel} ${lastUpdatedAt.toLocaleTimeString()}`;
  }, [lastUpdatedAt, t.updatedLabel]);

  const onboardingStats = useMemo(() => {
    void refreshTick;
    const total = Array.isArray(stories) ? stories.length : 0;
    let hasAnyCanon = 0;
    let hasAnyComments = 0;

    (stories || []).forEach((s) => {
      const scenes = Array.isArray(s?.scenes) ? s.scenes : [];
      const storyId = s?._id || s?.id;
      const canonCount = scenes.filter(
        (x) => x?.type === "canon" && x?.status === "approved"
      ).length;
      if (canonCount > 0) hasAnyCanon += 1;

      const comments = loadCommentsForStory(storyId);
      if (Array.isArray(comments) && comments.length > 0) hasAnyComments += 1;
    });

    return { total, hasAnyCanon, hasAnyComments };
  }, [stories, refreshTick]);

  const writer = spotlightView.writer;
  const book = spotlightView.book;
  const words = spotlightView.words;
  const wordsDateLabel = words.dateLabel || new Date().toLocaleDateString();
  const dailyWordsParts = splitDailyWordsText(words.text, lang);
  const writerTextParts = buildSpotlightTextParts(writer.fullText, writer.teaser, 2);
  const bookTextParts = buildSpotlightTextParts(book.fullText, book.teaser, 2);

  const canSend =
    String(fbText || "").trim().length > 0 &&
    (fbStoryId === GENERAL_FEEDBACK_ID || String(fbStoryId || "").trim().length > 0);

  const visibleLiveFeedback = useMemo(() => {
    const arr = Array.isArray(liveFeedback) ? liveFeedback.slice() : [];
    arr.sort((a, b) => {
      const ta = new Date(a?.createdAt || 0).getTime();
      const tb = new Date(b?.createdAt || 0).getTime();
      return liveFbSort === "oldest" ? ta - tb : tb - ta;
    });
    return arr.slice(0, liveFbLimit);
  }, [liveFeedback, liveFbSort, liveFbLimit]);

  const homeHeroBackgroundUrl = `${process.env.PUBLIC_URL || ""}/images/storyhub/storyhub-background.png`;

  return (
    <div
      className="page ns-home-page"
      style={{ "--ns-storyhub-bg": `url("${homeHeroBackgroundUrl}")` }}
    >
      <div
        className="card ns-home-hero-library"
        style={{ maxWidth: 1100, margin: "0 auto" }}
      >
        <p className="ns-home-hero-brand">NEXTSCENES</p>
        <h1
          style={{
            marginTop: 0,
            marginBottom: 6,
            fontSize: "clamp(28px, 3vw, 42px)",
            lineHeight: 1.06,
            whiteSpace: "normal",
            maxWidth: 820,
          }}
        >
          {t.heroTitleNew}
        </h1>
        <p style={{ marginTop: 0, color: "#475569", maxWidth: 760 }}>
          {t.heroDescNew}
        </p>
        <p className="ns-home-hero-origin">{t.heroOriginNew}</p>
      </div>

      <div
        className="card ns-home-start"
        style={{
          maxWidth: 1100,
          margin: "16px auto 0 auto",
          background: "#ecfdf5",
        }}
      >
        <div className="ns-home-start-head">
          <div>
            <h2 style={{ marginTop: 0, marginBottom: 6, fontSize: "clamp(24px, 2.4vw, 30px)", lineHeight: 1.15 }}>{t.startHereTitle}</h2>
            <p style={{ marginTop: 0, color: "#475569", maxWidth: 760 }}>
              {t.startHereDesc}
            </p>
          </div>

          <a
            className="ns-home-ai-principles-link"
            href={lang === "fr" ? "https://www.nextscenes.org/fr/principes-ia" : "https://www.nextscenes.org/ai-principles"}
            target="_blank"
            rel="noreferrer"
          >
            {t.aiPrinciplesLink}
          </a>
        </div>

        <div className="ns-home-ai-principles-note">
          <strong>{t.aiPrinciplesTitle}</strong>
          <span>{t.aiPrinciplesDesc}</span>
        </div>

        <div className="ns-steps">
          <div className="ns-step">
            <img src="/images/steps/explore.jpg" alt={t.startHereStep1Title} />
            <div>
              <strong>{t.startHereStep1Title}</strong>
              <p>{t.startHereStep1Desc}</p>
              <button
                className="ns-button ns-button-primary ns-home-step-action"
                type="button"
                onClick={goBrowseStories}
              >
                {t.startHereStep1Action}
              </button>
            </div>
          </div>

          <div className="ns-step">
            <img src="/images/steps/read.jpg" alt={t.startHereStep2Title} />
            <div>
              <strong>{t.startHereStep2Title}</strong>
              <p>{t.startHereStep2Desc}</p>
              <button
                className="ns-button ns-button-outline ns-home-step-action"
                type="button"
                onClick={goStartWriting}
              >
                {t.startHereStep2Action}
              </button>
            </div>
          </div>

          <div className="ns-step">
            <img src="/images/steps/write.jpg" alt={t.startHereStep3Title} />
            <div>
              <strong>{t.startHereStep3Title}</strong>
              <p>{t.startHereStep3Desc}</p>
              <button
                className="ns-button ns-button-outline ns-home-step-action"
                type="button"
                onClick={goWriterStudio}
              >
                {t.startHereStep3Action}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="card ns-home-beta"
        style={{
          maxWidth: 1100,
          margin: "12px auto 0 auto",
          background: "#f8fafc",
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 6 }}>{t.betaHelpTitle}</h3>
        <p style={{ marginTop: 0, color: "#475569" }}>{t.betaHelpDesc}</p>
        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={() => {
            const el = document.getElementById("live-feedback-section");
            if (el) el.scrollIntoView({ behavior: "smooth" });
          }}
        >
          {t.shareFeedback}
        </button>
      </div>

      {!onboardingDismissed ? (
        <div
          className="card"
          style={{
            maxWidth: 1100,
            margin: "16px auto 0 auto",
            background: "#f8fafc",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 260 }}>
              <h2 style={{ marginTop: 0, marginBottom: 6 }}>{t.quickStartTitle}</h2>
              <p style={{ marginTop: 0, color: "#475569" }}>{t.quickStartDesc1}</p>
              <p style={{ marginTop: 0, color: "#475569" }}>{t.quickStartDesc2}</p>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() => setShowOnboardingTour(true)}
              >
                🎬 {t.guidedTour}
              </button>
              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={dismissOnboarding}
              >
                {t.hide}
              </button>
            </div>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 12,
            }}
          >
            <div className="card" style={{ margin: 0 }}>
              <div style={{ fontWeight: 700 }}>1️⃣ {t.qs1Title}</div>
              <p style={{ marginTop: 8, color: "#475569" }}>{t.qs1Desc}</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                <button
                  className="ns-button ns-button-primary"
                  type="button"
                  onClick={goStartWriting}
                >
                  {t.qsStartWriting}
                </button>
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={goExploreForums}
                >
                  {t.qsExploreForums}
                </button>
              </div>
            </div>

            <div className="card" style={{ margin: 0 }}>
              <div style={{ fontWeight: 700 }}>2️⃣ {t.qs2Title}</div>
              <p style={{ marginTop: 8, color: "#475569" }}>{t.qs2Desc}</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={goWriterStudio}
                >
                  {t.qsOpenWriterStudio}
                </button>
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={goBrowseStories}
                >
                  {t.qsBrowseStories}
                </button>
              </div>
            </div>

            <div className="card" style={{ margin: 0 }}>
              <div style={{ fontWeight: 700 }}>3️⃣ {t.qs3Title}</div>
              <p style={{ marginTop: 8, color: "#475569" }}>{t.qs3Desc}</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={goBrowseStories}
                >
                  {t.qsBrowse}
                </button>
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={goMystery250}
                >
                  Mystery250
                </button>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12, color: "#475569" }}>
            Snapshot: <b>{onboardingStats.total}</b> storylines · <b>{onboardingStats.hasAnyCanon}</b> with canon · <b>{onboardingStats.hasAnyComments}</b> with comments
          </div>
        </div>
      ) : null}

      {showOnboardingTour ? (
        <ModalShell
          title={t.tourTitle}
          subtitle={t.tourSubtitle}
          onClose={() => setShowOnboardingTour(false)}
          maxWidth={980}
          zIndex={1700}
          closeLabel={t.close}
        >
          <div className="card" style={{ margin: 0 }}>
            <h3 style={{ marginTop: 0 }}>{t.tourHeader}</h3>
            <p style={{ color: "#475569", marginTop: 8 }}><b>{t.tour1}</b></p>
            <p style={{ color: "#475569", marginTop: 8 }}><b>{t.tour2}</b></p>
            <p style={{ color: "#475569", marginTop: 8 }}><b>{t.tour3}</b></p>
            <p style={{ color: "#475569", marginTop: 8 }}><b>{t.tour4}</b></p>
          </div>
        </ModalShell>
      ) : null}

      <div className="card ns-home-spotlight" style={{ maxWidth: 1100, margin: "16px auto 0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ marginTop: 0, marginBottom: 6, fontSize: "clamp(22px, 2.1vw, 28px)", lineHeight: 1.18 }}>{t.spotlightTitle}</h2>
            <p style={{ marginTop: 0, color: "#475569" }}>{t.spotlightSubtitle}</p>
          </div>
          <button className="ns-button ns-button-outline" type="button" onClick={reloadSpotlight}>
            {t.refresh}
          </button>
        </div>

        {spotErr ? <p style={{ color: "#b91c1c" }}>{spotErr}</p> : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 14,
          }}
        >
          <div
            className="card ns-home-spotlight-card ns-home-spotlight-writer"
            data-spotlight-label={lang === "fr" ? "AUTEUR" : "WRITER"}
            style={{ margin: 0 }}
          >
            <div className="ns-home-spotlight-card-head">
              <h3 style={{ marginTop: 0, marginBottom: 0 }}>{t.writerOfMonth}</h3>
            </div>
            {writer.imageUrl && writerImgOk ? (
              <div style={{ marginTop: 10 }}>
                <SpotlightImage
                  src={writer.imageUrl}
                  alt={writer.name}
                  variant="writer"
                  onError={() => setWriterImgOk(false)}
                />
              </div>
            ) : (
              <p style={{ color: "#475569" }}>{t.noImageYet}</p>
            )}
            <div style={{ marginTop: 10, fontWeight: 700 }}>{writer.name}</div>
            {writerTextParts.teaser ? (
              <p className="ns-home-spotlight-teaser">{writerTextParts.teaser}</p>
            ) : null}

            {writerTextParts.remainder ? (
              <details className="ns-home-spotlight-reveal">
                <summary>{t.readMore}</summary>
                <div>
                  {splitParagraphs(writerTextParts.remainder).map((paragraph, index) => (
                    <p key={`writer-full-${index}`}>{paragraph}</p>
                  ))}
                </div>
              </details>
            ) : null}
          </div>

          <div
            className="card ns-home-spotlight-card ns-home-spotlight-book"
            data-spotlight-label={lang === "fr" ? "LIVRE" : "BOOK"}
            style={{ margin: 0 }}
          >
            <div className="ns-home-spotlight-card-head">
              <h3 style={{ marginTop: 0, marginBottom: 0 }}>{t.bookOfMonth}</h3>
            </div>
            {book.coverUrl && bookImgOk ? (
              <div style={{ marginTop: 10 }}>
                <SpotlightImage
                  src={book.coverUrl}
                  alt={book.title}
                  variant="book"
                  onError={() => setBookImgOk(false)}
                />
              </div>
            ) : (
              <p style={{ color: "#475569" }}>{t.noCoverYet}</p>
            )}
            <div style={{ marginTop: 10, fontWeight: 700 }}>{book.title}</div>
            {bookTextParts.teaser ? (
              <p className="ns-home-spotlight-teaser">{bookTextParts.teaser}</p>
            ) : null}

            {bookTextParts.remainder ? (
              <details className="ns-home-spotlight-reveal">
                <summary>{t.readMore}</summary>
                <div>
                  {splitParagraphs(bookTextParts.remainder).map((paragraph, index) => (
                    <p key={`book-full-${index}`}>{paragraph}</p>
                  ))}
                </div>
              </details>
            ) : null}
          </div>

          <div
            className="card ns-home-spotlight-card ns-home-spotlight-daily ns-home-daily-words-card"
            data-spotlight-label={lang === "fr" ? "PAROLES DU JOUR" : "DAILY WORDS"}
            style={{ margin: 0 }}
          >
            <div className="ns-home-spotlight-card-head">
              <h3 style={{ marginTop: 0, marginBottom: 0 }}>{t.dailyWords}</h3>
            </div>

            <div className="ns-home-daily-words-body">
              {dailyWordsParts.english ? (
                <section className="ns-home-daily-words-section">
                  <div className="ns-home-daily-words-label">English</div>
                  {splitParagraphs(dailyWordsParts.english).map((paragraph, index) => (
                    <p key={`daily-words-en-${index}`}>{paragraph}</p>
                  ))}
                </section>
              ) : null}

              {dailyWordsParts.french ? (
                <section className="ns-home-daily-words-section">
                  <div className="ns-home-daily-words-label">Français</div>
                  {splitParagraphs(dailyWordsParts.french).map((paragraph, index) => (
                    <p key={`daily-words-fr-${index}`}>{paragraph}</p>
                  ))}
                </section>
              ) : null}
            </div>

            <div style={{ marginTop: 10, fontWeight: 700 }}>{words.author}</div>
            <div style={{ color: "#64748b", fontSize: 13 }}>{wordsDateLabel}</div>
          </div>
        </div>
      </div>

      {showStoryPicker ? (
        <ModalShell title={t.searchAllShort} subtitle={t.chooseStorylinePrompt} onClose={() => setShowStoryPicker(false)} closeLabel={t.close}>
          <div style={{ display: "grid", gap: 10 }}>
            <input
              value={storySearch}
              onChange={(e) => setStorySearch(e.target.value)}
              className="ns-input"
              placeholder={t.searchAllShort}
            />
            <div style={{ maxHeight: 420, overflowY: "auto", display: "grid", gap: 8 }}>
              {storySearchResults.map((s) => {
                const id = storyIdOf(s);
                return (
                  <button
                    key={id}
                    type="button"
                    className="ns-button ns-button-outline"
                    style={{ justifyContent: "flex-start" }}
                    onClick={() => {
                      setFbStoryId(id);
                      setShowStoryPicker(false);
                    }}
                  >
                    {storyTitleOf(s)}
                  </button>
                );
              })}
            </div>
          </div>
        </ModalShell>
      ) : null}

      {showBetaFeedbackModal ? (
        <ModalShell
          title={t.betaFeedbackTitle}
          subtitle={betaErr || "See your submitted beta feedback and admin replies"}
          onClose={() => setShowBetaFeedbackModal(false)}
          closeLabel={t.close}
          maxWidth={1100}
        >
          <div style={{ display: "grid", gap: 14 }}>
            <div className="card" style={{ margin: 0 }}>
              <h3 style={{ marginTop: 0, marginBottom: 10 }}>Send new beta feedback</h3>
              <form onSubmit={sendBetaFeedback} style={{ display: "grid", gap: 10 }}>
                <textarea
                  value={betaDraft}
                  onChange={(e) => setBetaDraft(e.target.value)}
                  className="ns-input"
                  style={{ minHeight: 110, resize: "vertical" }}
                  placeholder="Tell us what confused you, what worked, or what feels rough."
                />
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <button type="submit" className="ns-button ns-button-primary" disabled={betaBusy || !betaDraft.trim()}>
                    {betaBusy ? "Sending..." : t.sendShort}
                  </button>
                  <button type="button" className="ns-button ns-button-outline" onClick={() => reloadBetaFeedback(true)}>
                    {t.refreshShort}
                  </button>
                  {betaSendMsg ? <span style={{ color: "#475569", fontSize: 13 }}>{betaSendMsg}</span> : null}
                </div>
              </form>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(280px, 340px) 1fr",
                gap: 14,
                alignItems: "start",
              }}
            >
              <div className="card" style={{ margin: 0, maxHeight: 520, overflowY: "auto" }}>
                <h3 style={{ marginTop: 0, marginBottom: 10 }}>Your submissions</h3>
                {betaThreadList.length === 0 ? (
                  <p style={{ margin: 0, color: "#475569" }}>
                    No beta feedback yet. Send one above and it will show here.
                  </p>
                ) : (
                  <div style={{ display: "grid", gap: 8 }}>
                    {betaThreadList.map((item) => {
                      const pid = String(item?.id || "").trim();
                      const replies = betaRepliesByParent[pid] || [];
                      const isOpen = openBetaThreadId === pid;
                      return (
                        <button
                          key={pid}
                          type="button"
                          className="ns-button ns-button-outline"
                          style={{ justifyContent: "space-between", gap: 10 }}
                          onClick={() => setOpenBetaThreadId(pid)}
                        >
                          <span style={{ textAlign: "left", whiteSpace: "normal" }}>
                            {String(item?.text || "").trim().slice(0, 90) || "Untitled"}
                          </span>
                          <span style={{ fontSize: 12, color: isOpen ? "#0f172a" : "#475569" }}>
                            {replies.length} replies
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="card" style={{ margin: 0 }}>
                {openBetaThreadId ? (
                  (() => {
                    const top = betaThreadList.find((x) => String(x?.id || "") === openBetaThreadId);
                    const replies = betaRepliesByParent[openBetaThreadId] || [];
                    if (!top) return <p style={{ margin: 0, color: "#475569" }}>Select one of your submissions to view the full thread.</p>;
                    return (
                      <div style={{ display: "grid", gap: 12 }}>
                        <div className="banner">
                          <div style={{ fontWeight: 700, marginBottom: 6 }}>Original message</div>
                          <div style={{ color: "#475569", whiteSpace: "pre-line" }}>{top.text}</div>
                        </div>
                        {replies.map((reply) => (
                          <div key={reply.id} className="banner">
                            <div style={{ fontWeight: 700, marginBottom: 6 }}>{reply.author || "Admin"}</div>
                            <div style={{ color: "#475569", whiteSpace: "pre-line" }}>{reply.text}</div>
                          </div>
                        ))}
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          <button type="button" className="ns-button ns-button-outline" onClick={() => markBetaThreadSeen(openBetaThreadId, replies)}>
                            Mark as read
                          </button>
                          <button type="button" className="ns-button ns-button-outline" onClick={() => reloadBetaFeedback(true)}>
                            Refresh thread
                          </button>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <p style={{ margin: 0, color: "#475569" }}>
                    Select one of your submissions to view the full thread.
                  </p>
                )}
              </div>
            </div>
          </div>
        </ModalShell>
      ) : null}

      <>
        <div className="card ns-home-trending" style={{ maxWidth: 1100, margin: "16px auto 0 auto" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <h2 style={{ marginTop: 0, marginBottom: 0, fontSize: "clamp(22px, 2.1vw, 28px)", lineHeight: 1.18 }}>
              {t.trendingStorylinesTitle}
            </h2>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() => reloadTrending(true)}
                disabled={trendBusy}
              >
                {t.refreshShort}
              </button>
              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={goBrowseStories}
              >
                {t.browseShort}
              </button>
            </div>
          </div>

          <p style={{ marginTop: 8, color: "#475569" }}>
            {t.trendingStorylinesDesc}
          </p>

          {trendErr ? (
            <p style={{ color: "#b91c1c", marginTop: 8 }}>{trendErr}</p>
          ) : null}

          {trendBusy ? (
            <p style={{ color: "#475569", marginTop: 8 }}>{t.refreshingTrending}</p>
          ) : null}

          {trendingItems.length === 0 ? (
            <p style={{ color: "#475569" }}>{t.noTrendingYet}</p>
          ) : (
            <div className="ns-home-trending-grid" style={{ marginTop: 10, display: "grid", gap: 10 }}>
              {trendingItems.map((item, trendIndex) => {
                const story = {
                  _id: item.id,
                  id: item.id,
                  title: item.title,
                  forum: item.forum,
                  author: item.author,
                  writingMode: item.writingMode,
                  coverImageUrl: item.coverUrl,
                  description: item.description,
                };

                return (
                  <div key={item.id} className="banner ns-home-trending-card" data-rank={trendIndex + 1}>
                    <div className="ns-home-trending-cover-wrap">
                      {item.coverUrl ? (
                        <img
                          className="ns-home-trending-cover"
                          src={item.coverUrl}
                          alt={item.title ? `${item.title} cover` : "Storyline cover"}
                          loading="lazy"
                        />
                      ) : (
                        <div className="ns-home-trending-cover ns-home-trending-cover-fallback" aria-label={t.noCoverStoryline || "No cover yet"}>
                          <span>{getInitials(item.title)}</span>
                        </div>
                      )}
                      <span className="ns-home-trending-rank">#{trendIndex + 1}</span>
                    </div>

                    <div className="ns-home-trending-body">
                      <div className="ns-home-trending-status">
                        {activityLabelForBand(item.activityBand, t)}
                      </div>

                      <h3 className="ns-home-trending-title">
                        {item.title || "Untitled Storyline"}
                      </h3>

                      <div className="ns-home-trending-meta">
                        {item.author ? `${t.byLabel || "By"} ${item.author} · ` : ""}
                        {item.forum || t.unclassifiedForum || "Unclassified Forum"}
                      </div>

                      {item.description ? (
                        <p className="ns-home-trending-teaser">{item.description}</p>
                      ) : null}

                      <div className="ns-home-trending-footer">
                        <button
                          className="ns-button ns-button-outline"
                          type="button"
                          onClick={() => openAndHighlightStory(story)}
                        >
                          {t.openShort}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div
          id="live-feedback-section"
          className="card ns-home-live-feedback"
          style={{ maxWidth: 1100, margin: "16px auto 0 auto" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <h2 style={{ marginTop: 0, marginBottom: 0, fontSize: "clamp(22px, 2.1vw, 28px)", lineHeight: 1.18 }}>{t.liveFeedbackTitle}</h2>

            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() => {
                  openBetaModalFromToast();
                }}
                title="See your submitted beta feedback and admin replies"
              >
                {t.betaFeedbackTitle}
                {betaNewReplyCount > 0 ? (
                  <span
                    style={{
                      marginLeft: 8,
                      display: "inline-block",
                      minWidth: 18,
                      padding: "1px 6px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 800,
                      background: "rgba(59,130,246,0.12)",
                      border: "1px solid rgba(59,130,246,0.30)",
                      color: "rgba(30,64,175,1)",
                      lineHeight: "18px",
                      textAlign: "center",
                    }}
                  >
                    {betaNewReplyCount}
                  </span>
                ) : null}
              </button>

              {heartbeatText ? (
                <span style={{ color: "#475569", fontSize: 13 }}>{heartbeatText}</span>
              ) : null}
              <button className="ns-button ns-button-outline" type="button" onClick={goExploreForums}>
                {t.exploreShort}
              </button>
            </div>
          </div>

          <p style={{ marginTop: 8, color: "#475569" }}>{t.chooseStorylinePrompt}</p>

          <form onSubmit={submitLiveFeedback} style={{ marginTop: 10, display: "grid", gap: 8 }}>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <select
                value={fbStoryId}
                onChange={(e) => setFbStoryId(e.target.value)}
                className="ns-input ns-livefb-field ns-livefb-select"
                style={{ minWidth: 260 }}
              >
                <option value={GENERAL_FEEDBACK_ID}>{t.generalFeedbackLabel}</option>

                {recentStories.length > 0 ? (
                  <optgroup label={t.recentStorylinesShort}>
                    {recentStories.map((s) => {
                      const id = storyIdOf(s);
                      return (
                        <option key={`recent_${id}`} value={id}>
                          {storyTitleOf(s)}
                        </option>
                      );
                    })}
                  </optgroup>
                ) : null}

                {trendingStoriesForDropdown.length > 0 ? (
                  <optgroup label={t.trendingStorylinesShort}>
                    {trendingStoriesForDropdown.map((s) => {
                      const id = storyIdOf(s);
                      return (
                        <option key={`trend_${id}`} value={id}>
                          {storyTitleOf(s)}
                        </option>
                      );
                    })}
                  </optgroup>
                ) : null}
              </select>

              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() => {
                  setShowStoryPicker(true);
                  setStorySearch("");
                }}
              >
                {t.searchAllShort}
              </button>

              <button
                type="submit"
                className="ns-button ns-button-primary"
                disabled={liveFbBusy || !canSend || !canPostDiscussion}
              >
                {t.sendShort}
              </button>

              {liveFbBusy ? (
                <span style={{ color: "#475569", fontSize: 13 }}>{t.refreshingShortLabel}</span>
              ) : null}
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <AvatarCircle
                name={currentUser?.name || currentUser?.username || "You"}
                avatarUrl={currentUser?.avatarUrl}
                size={34}
              />
              <textarea
                value={fbText}
                disabled={!canPostDiscussion}
                onChange={(e) => setFbText(e.target.value)}
                placeholder={t.shareThoughtPlaceholder}
                className="ns-input ns-livefb-field ns-livefb-textarea"
                style={{ minHeight: 70, resize: "vertical", flex: 1 }}
              />
            </div>

            {liveFbErr || fbSentMsg ? (
              <div
                style={{
                  color: liveFbErr ? "#b91c1c" : "#166534",
                  fontSize: 13,
                }}
              >
                {liveFbErr || fbSentMsg}
              </div>
            ) : null}
          </form>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginTop: 10,
              alignItems: "center",
            }}
          >
            <button
              type="button"
              className="ns-button ns-button-outline"
              onClick={() => setLiveFbSort((s) => (s === "newest" ? "oldest" : "newest"))}
            >
              {t.sortPrefix} {liveFbSort === "newest" ? t.newestShort : t.oldestShort}
            </button>

            <button
              type="button"
              className="ns-button ns-button-outline"
              onClick={() => {
                setLiveFbLimit(20);
                reloadLiveFeedback(true, true);
              }}
              disabled={liveFbBusy}
            >
              {t.refreshShort}
            </button>

            <div style={{ marginLeft: "auto", color: "#475569", fontSize: 13 }}>
              {t.showingLabel} {Math.min(liveFbLimit, liveFeedback.length)} {t.ofLabel} {liveFeedback.length}
            </div>
          </div>

          {liveFeedback.length === 0 ? (
            <p style={{ color: "#475569" }}>{t.noFeedbackYet}</p>
          ) : (
            <>
              <div
                className="ns-livefb-scroll"
                style={{
                  marginTop: 10,
                  display: "grid",
                  gap: 10,
                  maxHeight: 520,
                  overflowY: "auto",
                  paddingRight: 6,
                }}
              >
                {visibleLiveFeedback.map((x, idx) => {
                  const storyId = String(x?.storyId || "").trim();
                  const isGeneral = !storyId;
                  const story =
                    stories.find((s) => String(s?._id || s?.id || "") === storyId) || null;

                  const key = `${storyId || "general"}_${x?.id || idx}`;
                  const fullText = String(x?.text || "").trim();
                  const isExpanded = !!expandedLiveFb[key];
                  const shortText = fullText.length > 220 ? `${fullText.slice(0, 220)}…` : fullText;
                  const showText = isExpanded ? fullText : shortText;

                  const label = feedbackSourceLabel(x);
                  const { isWs, cardStyle, badgeStyle } = feedbackSourceStyles(x);
                  const isReply = !!String(x?.parentId || "").trim();

                  return (
                    <div
                      key={key}
                      className="banner ns-home-feedback-card"
                      style={{
                        ...(cardStyle || {}),
                        borderRadius: 12,
                        paddingLeft: 12,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            color: "#475569",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "3px 8px",
                              borderRadius: 999,
                              fontSize: 12,
                              fontWeight: 800,
                              letterSpacing: "0.02em",
                              ...(badgeStyle || {}),
                            }}
                            title={
                              isWs
                                ? "Reader Feedback coming from WriterStudio"
                                : "Feedback sent from Home"
                            }
                          >
                            {label}
                          </span>

                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            <AvatarCircle
                              name={x?.author || "Anonymous"}
                              avatarUrl={x?.avatarUrl}
                              size={28}
                            />
                            <span>
                              <b>{x?.author || "Anonymous"}</b> · {formatDateTime(x?.createdAt)}
                              {isReply ? <span style={{ marginLeft: 8 }}>· Reply</span> : null}
                            </span>
                          </span>
                        </div>

                        <div style={{ color: "#64748b", fontSize: 12 }}>
                          {x?.target === "storyline" ? t.storylineLabel : t.generalLabel}
                        </div>
                      </div>

                      <div style={{ marginTop: 6, color: "#475569" }}>
                        <b>
                          {isGeneral
                            ? t.generalFeedbackLabel
                            : x?.storyTitle || story?.title || "Untitled Storyline"}
                        </b>{" "}
                        · {isGeneral ? "NextScenes" : x?.forum || story?.forum || "Unclassified Forum"}
                      </div>

                      <div style={{ marginTop: 8, color: "#475569", whiteSpace: "pre-line" }}>
                        {showText || "…"}
                      </div>

                      {fullText.length > 220 ? (
                        <div style={{ marginTop: 10 }}>
                          <button
                            type="button"
                            className="ns-button ns-button-outline"
                            onClick={() => {
                              setExpandedLiveFb((m) => ({ ...(m || {}), [key]: !m?.[key] }));
                            }}
                          >
                            {isExpanded ? t.showLessShort : t.readMoreShort}
                          </button>
                        </div>
                      ) : null}

                      {!isGeneral ? (
                        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button
                            className="ns-button ns-button-outline"
                            type="button"
                            onClick={() => {
                              if (story) openAndHighlightStory(story);
                              else
                                openAndHighlightStory({
                                  _id: storyId,
                                  id: storyId,
                                  title: x?.storyTitle || "Storyline",
                                  forum: x?.forum || "",
                                });
                            }}
                          >
                            {t.openShort}
                          </button>

                          <button
                            className="ns-button ns-button-outline"
                            type="button"
                            onClick={() => {
                              if (story) openThreadInWriterStudio(story, x?.id);
                            }}
                          >
                            {t.discussShort}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {liveFeedback.length > visibleLiveFeedback.length ? (
                <div style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    className="ns-button ns-button-outline"
                    onClick={() => setLiveFbLimit((n) => n + 20)}
                  >
                    {t.loadMore}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </>

      {betaToast?.show ? (
        <div
          style={{
            position: "fixed",
            right: 18,
            bottom: 18,
            zIndex: 2200,
            background: "#0f172a",
            color: "#ffffff",
            padding: "10px 12px",
            borderRadius: 12,
            boxShadow: "0 18px 40px rgba(15,23,42,0.20)",
            maxWidth: 360,
          }}
          role="status"
          aria-live="polite"
        >
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 13, lineHeight: 1.2 }}>
              {betaToast.msg}
            </div>
            <button
              type="button"
              className="ns-button ns-button-outline"
              style={{
                padding: "6px 10px",
                borderColor: "rgba(255,255,255,0.35)",
                color: "#ffffff",
                background: "transparent",
              }}
              onClick={() => {
                openBetaModalFromToast();
              }}
            >
              {t.openShort}
            </button>
          </div>
        </div>
      ) : null}

      <div style={{ height: 18 }} />
    </div>
  );
}
